/**
 * Returns the base URL for the current environment.
 *
 * This function determines the base URL using a hybrid approach:
 * - Client-side: Uses runtime detection via window.location.origin
 * - Server-side: Checks runtime override (BASE_URL) first, then falls back to build-time value (NEXT_PUBLIC_BASE_URL)
 *
 * @returns The base URL including protocol and domain (e.g., 'https://www.pixum.de')
 *
 * @remarks
 * **Client-side (browser):**
 * - Uses `window.location.origin` to get the current origin
 * - Always reflects the actual domain the user is visiting
 *
 * **Server-side (build time & runtime):**
 * - First checks `BASE_URL` (runtime override via Helm for draft mode/preview)
 * - Falls back to `NEXT_PUBLIC_BASE_URL` (build-time value for static generation)
 * - This allows static pages to use build-time URL while draft mode can override at runtime
 *
 * @example
 * ```typescript
 * // Client-side in browser
 * const baseUrl = getBaseUrl(); // 'https://www.pixum.de' (from window.location)
 *
 * // Server-side during static generation
 * const baseUrl = getBaseUrl(); // Uses NEXT_PUBLIC_BASE_URL from build
 *
 * // Server-side during draft mode (runtime override)
 * const baseUrl = getBaseUrl(); // Uses BASE_URL from Helm if set
 * ```
 */
export function getBaseUrl(): string {
  // Client-side: use window.location.origin for accurate runtime URL
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  // Server-side: check runtime override first, then fall back to build-time value
  // BASE_URL: Runtime override (set via Helm for draft mode/preview deployments)
  // NEXT_PUBLIC_BASE_URL: Build-time value (embedded during Docker build for static generation)
  return process.env['BASE_URL'] || process.env['NEXT_PUBLIC_BASE_URL'] || '';
}
